import {Button} from "@blueprintjs/core";
import {ButtonContainer, FGListen, FieldSet, showError, showSuccess} from "nsitools-react";
import * as React from "react";
import {atom, useSetRecoilState} from "recoil";
import * as Yup from "yup";

import {
    BroodRequestApi,
    DogApi,
    DogBroodRequestGetDto,
    ELabStatus,
    SaveDogBroodRequestTransientDto,
} from "../../../../api";
import {SmallFormGenerator} from "../../../../components";
import {useDialog} from "../../../../contexts";
import {ERoutes} from "../../../../ERoutes";
import {useApiService, useCrudApi, useTl} from "../../../../hooks";
import {useReferential} from "../../../../hooks/useReferential";
import {ETLCodes} from "../../../../locales";
import {PuppyCard} from "./PuppyCard";
import {toUpperCamelCase} from "../../../../utils";

export interface IPuppiesTabProps {
    isExecuted: boolean;
    broodRequestId: number;
    requestId: number;
    editMode: boolean;
    refreshPuppies: boolean;
    isRefreshed: (isRefreshed: boolean) => void;
    isLoading: boolean;
}

export const ArePuppiesDirty = atom<boolean>({
    key: "ArePuppiesDirty",
    default: false,
});

export const PuppiesTab: React.FunctionComponent<IPuppiesTabProps> = ({
                                                                          broodRequestId,
                                                                          requestId,
                                                                          editMode,
                                                                          refreshPuppies,
                                                                          isRefreshed,
                                                                          isLoading,
                                                                          isExecuted,
                                                                      }) => {
    const setArePuppiesDiry = useSetRecoilState(ArePuppiesDirty);
    const api = useApiService(BroodRequestApi);
    const dogApi = useApiService(DogApi);
    const {t} = useTl();
    const {showDialogPromise} = useDialog();

    const {data, loading, saving, saveItem, refresh} = useCrudApi<DogBroodRequestGetDto>(
        React.useMemo(
            () => ({
                getApiFn: async (id = broodRequestId) => id && id !== 0 && (await api.getDogBroodRequestTransient(id)),
                saveApiFn: async (d: DogBroodRequestGetDto) => {
                    return await api.saveDogBroodRequestTransient(
                        SaveDogBroodRequestTransientDto.fromJS({
                            puppies: d.puppies.map((p) => {
                                return {...p, name: toUpperCamelCase(p.name)};
                            }),
                            definitiveSave: false,
                        })
                    );
                },
                onSavedRoute: (d) => `${ERoutes.broodRequest}/${broodRequestId}/puppies/edit`,
                deleteApiFn: () => {
                },
            }),
            [api, broodRequestId]
        ),
        [broodRequestId]
    );

    const canRegenLoshPedigree = React.useMemo(
        () => !loading && !data?.puppies?.find((c) => !c.isAlsh && !c.deceased) && isExecuted,
        [data?.puppies, isExecuted, loading]
    );

    const [regenLoshPedigreesLoading, setregenLoshPedigreesLoading] = React.useState(false);

    const regenLoshPedigrees = React.useCallback(async () => {
        setregenLoshPedigreesLoading(true);
        try {
            await api.switchToLoshPedigree(requestId);
            refresh();
            showSuccess(t(ETLCodes.Success));
        } catch {
            showError(t(ETLCodes.Error));
        }
        setregenLoshPedigreesLoading(false);
    }, [api, refresh, requestId, t]);

    React.useEffect(() => {
        if (data !== null) {
            if (refreshPuppies) {
                refresh();
                isRefreshed(true);
            }
        }
    }, [data, isRefreshed, refresh, refreshPuppies]);

    const [Breed, BreedLoading] = useReferential(
        (c) => (data?.motherBreedId ? c.getBreedVarietiesWithCurrentBreed(data?.motherBreedId) : null),
        true,
        [data]
    );

    const completePuppy = React.useCallback((parent: any, fieldName: string) => {
        console.log(
            `KnownValues:\nName:${parent.name}\nOwners length:${parent.owners?.length}\nColorId=${parent.colorId}\nBreedId:${parent.breedId}`
        );
        if (parent.deceased) {
            return true;
        }
        if (parent.name !== null && parent.colorId !== null && parent.breedId !== null && parent.owners?.length > 0) {
            return true;
        }
        if ((parent.name === null || parent.name === "") && parent.colorId === null && parent.owners?.length === 0) {
            return true;
        }
        if (!parent[fieldName]) {
            return false;
        }
        return true;
    }, []);

    const [Pedigree] = useReferential((c) => c.getPuppyPedigrees(), true, [data]);

    const FormSchema = React.useMemo(() => {
        return Yup.object().shape({
            puppies: Yup.array().of(
                Yup.object().shape({
                    name: Yup.string()
                        .nullable()
                        .test("correct-year", t(ETLCodes.NameDoesNotStartWithCalendarLetter), async function (value) {
                            if (value !== null && value !== undefined && value !== "") {
                                console.log("Value of name is ", value);
                                if (!this.parent.deceased && this.parent?.labStatus === ELabStatus.AnalysisDone) {
                                    return value.charAt(0) === data.currentLetterYear;
                                }
                            }
                            return true;
                        })
                        .test("complete-puppy", t(ETLCodes.PuppyInformationIncomplete), async function () {
                            return completePuppy(this.parent, "name");
                        })
                        .test("name-unicity", t(ETLCodes.NameAlreadyTaken), async function (value) {
                            if (!this.parent.kennelId || !!this.parent.correspondingDogId || !value) {
                                return true;
                            }
                            var res = await dogApi.getDogUnicityQuery(this.parent.name, this.parent.kennelId);

                            return res.value;
                        }),
                    puppyOwnerEmail: Yup.string()
                        .nullable()
                        .test("is-correct-email", t(ETLCodes.InvalidEmail), async function (value) {
                            if (value !== null && value !== undefined && value !== "") {
                                const regexp = /(.*)[@]{1}(.*)[.]{1}([a-z]*)/g;
                                const matching = regexp.test(value);
                                return matching;
                            }
                            return true;
                        }),
                    colorId: Yup.number()
                        .nullable()
                        .test("complete-puppy-colorId", t(ETLCodes.PuppyInformationIncomplete), async function () {
                            return completePuppy(this.parent, "colorId");
                        }),
                    countryPedigreePrefixId: Yup.number()
                        .nullable()
                        .test("not-nullable", t(ETLCodes.Required), async function (value) {
                            if (!this.parent.deceased) {
                                return value !== null && value !== undefined;
                            }
                            return true;
                        }),
                })
            ),
        });
    }, [completePuppy, data, dogApi, t]);

    const [loadingReprintPedigrees, setloadingReprintPedigrees] = React.useState(false);
    const reprintPedigrees = React.useCallback(async () => {
        setloadingReprintPedigrees(true);
        try {
            await api.reprintPedigrees(broodRequestId);
            showSuccess(t(ETLCodes.Success));
        } catch {
            showError(t(ETLCodes.Error));
        } finally {
            setloadingReprintPedigrees(false);
        }
    }, [api, broodRequestId, t]);

    const [loadingResendSample, setloadingResendSample] = React.useState(false);

    const resendPedigreeSample = React.useCallback(async () => {
        const dialogResult = await showDialogPromise({
            message: t(ETLCodes.confirmRegenAndResendPedigreeSample),
            title: t(ETLCodes.PedigreeResendTitle),
        });
        if (dialogResult !== "yes") {
            return;
        }
        setloadingResendSample(true);
        try {
            await api.generatePedigreeSample(requestId);
            showSuccess(t(ETLCodes.Success));
        } catch {
            showError(t(ETLCodes.Error));
        } finally {
            setloadingResendSample(false);
        }
    }, [api, requestId, showDialogPromise, t]);
    return (
        <SmallFormGenerator
            initialValues={data}
            onSubmit={saveItem}
            editMode={editMode}
            loading={loading && BreedLoading}
            saving={saving}
            validationSchema={FormSchema}
            validateOnChange={false}
        >
            <FieldSet
                title={t(ETLCodes.Puppies)}
                rightElement={
                    <ButtonContainer>
                        {canRegenLoshPedigree && (
                            <Button
                                loading={regenLoshPedigreesLoading}
                                onClick={regenLoshPedigrees}
                                text={t(ETLCodes.SwitchToLoshPedigrees)}
                                intent="primary"
                                icon="exchange"
                                minimal
                            />
                        )}
                        {!!data?.puppies?.find((p) => p.correspondingDogId !== null) ? (
                            <Button
                                loading={loadingReprintPedigrees}
                                onClick={reprintPedigrees}
                                text={t(ETLCodes.ReprintPedigrees)}
                                intent="primary"
                                icon="print"
                                minimal
                            />
                        ) : null}
                        <Button
                            loading={loadingResendSample}
                            onClick={resendPedigreeSample}
                            text={t(ETLCodes.ResendPedigreeSample)}
                            intent="primary"
                            icon="envelope"
                            minimal
                        />
                    </ButtonContainer>
                }
            >
                <FGListen field="puppies" onChanged={(v, formik) => setArePuppiesDiry(data !== formik.values)}/>
                {data?.puppies?.map((puppy, index) => (
                    <PuppyCard
                        key={index}
                        index={index}
                        puppy={puppy}
                        breeds={Breed}
                        pedigrees={Pedigree}
                        refresh={refresh}
                        yearLetter={data.currentLetterYear}
                        isLoading={isLoading}
                    />
                ))}
            </FieldSet>
        </SmallFormGenerator>
    );
};
